/**
 * AidsCalculatorFunnel — Funnel de calcul des aides à la rénovation.
 *
 * Orchestrateur des 6 étapes :
 *   1. Profil + Ville
 *   2. Logement
 *   3. Revenus (sauté pour copro / locataire)
 *   4. Travaux (par catégories, avec tags d'éligibilité)
 *   5. Contact (nom, prénom, téléphone, adresse)
 *   6. Résultat
 *
 * Animations Framer Motion entre étapes + bouton retour contextuel
 * + analytics PostHog (un event par changement d'étape).
 */

import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { easeStripe } from '../../../lib/motion';
import {
  trackFunnelStart,
  trackStepView,
  trackFunnelAbandon,
  type FunnelContext,
} from '../../../lib/tracking';
import { FunnelStepper } from '../../ui/funnel-stepper';
import { Step1Profile } from './Step1Profile';
import { Step2Logement } from './Step2Logement';
import { Step3Revenus } from './Step3Revenus';
import { Step4Travaux } from './Step4Travaux';
import { Step5Contact } from './Step5Contact';
import { Step6Resultat } from './Step6Resultat';
import {
  ALL_STEP_LABELS,
  profileNeedsRevenus,
  useAidsStore,
  type FunnelStep,
  type ProfileType,
} from './useAidsStore';

/**
 * Mapping de l'étape logique (0..5) vers l'index visible dans le Stepper.
 * Si le profil ne nécessite pas de revenus, l'étape Revenus est masquée.
 */
function getVisibleSteps(needsRevenus: boolean): { id: string; label: string }[] {
  const all = [0, 1, 2, 3, 4, 5] as const;
  return all
    .filter((idx) => (idx === 2 ? needsRevenus : true))
    .map((idx) => ({ id: String(idx), label: ALL_STEP_LABELS[idx]! }));
}

function getVisibleIndex(step: FunnelStep, needsRevenus: boolean): number {
  // Position de l'étape courante dans la liste des étapes visibles.
  let visible = -1;
  for (let i = 0; i <= step; i++) {
    if (i === 2 && !needsRevenus) continue;
    visible++;
  }
  return Math.max(0, visible);
}

interface AidsCalculatorFunnelProps {
  /**
   * Pré-sélectionne un profil et saute l'étape 1.
   * Utile pour les pages d'audience verrouillée (ex. copropriétés).
   */
  initialProfile?: ProfileType;
}

export function AidsCalculatorFunnel({ initialProfile }: AidsCalculatorFunnelProps = {}) {
  const step = useAidsStore((s) => s.step);
  const dir = useAidsStore((s) => s.dir);
  const profile = useAidsStore((s) => s.profile);
  const setStep = useAidsStore((s) => s.setStep);
  const setProfile = useAidsStore((s) => s.setProfile);
  const goBack = useAidsStore((s) => s.goBack);

  // Pré-sélectionne le profil et saute directement à l'étape logement si nécessaire
  useEffect(() => {
    if (initialProfile && !profile) {
      setProfile(initialProfile);
      setStep(1, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProfile]);

  const needsRevenus = profileNeedsRevenus(profile);
  const visibleSteps = getVisibleSteps(needsRevenus);
  const visibleIndex = getVisibleIndex(step, needsRevenus);

  // Auto-skip l'étape Revenus si le profil ne la nécessite pas (forward only).
  useEffect(() => {
    if (step === 2 && !needsRevenus) {
      setStep(dir >= 0 ? 3 : 1, dir);
    }
  }, [step, needsRevenus, dir, setStep]);

  const aidsCtx: FunnelContext = {
    funnelId: 'aids_calculator',
    intent: 'renover',
    source: 'calculator',
  };

  // funnel_start au montage initial
  const aidsDidStart = useRef(false);
  useEffect(() => {
    if (!aidsDidStart.current) {
      aidsDidStart.current = true;
      trackFunnelStart(aidsCtx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // step_view à chaque changement d'étape.
  const prevStep = useRef(step);
  useEffect(() => {
    if (step !== prevStep.current) {
      prevStep.current = step;
      trackStepView(aidsCtx, ALL_STEP_LABELS[step] ?? `step_${step}`, step);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Abandon au déchargement
  useEffect(() => {
    const onUnload = () => {
      if (step < 5) {
        trackFunnelAbandon(aidsCtx, ALL_STEP_LABELS[step] ?? `step_${step}`, step);
      }
    };
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Scroll to top du funnel à chaque changement d'étape (utile sur mobile)
  // + focus du contenu de l'étape une fois la transition jouée (a11y clavier/SR).
  // Le premier rendu est ignoré pour ne pas voler le focus au chargement.
  const skippedFirstFocus = useRef(false);
  useEffect(() => {
    const el = document.getElementById('aids-funnel-top');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (!skippedFirstFocus.current) {
      skippedFirstFocus.current = true;
      return;
    }
    const t = window.setTimeout(() => {
      document.getElementById('aids-step-content')?.focus({ preventScroll: true });
    }, 300);
    return () => window.clearTimeout(t);
  }, [step]);

  const canGoBack = step > 0 && step !== 5;

  return (
    <div
      id="aids-funnel-top"
      className="relative overflow-hidden rounded-2xl border border-stone-200/70 bg-white shadow-[0_8px_32px_-8px_rgba(10,10,10,.10)]"
    >
      {/* ── Header — stepper ──────────────────────────────── */}
      <div className="border-b border-stone-200/80 bg-gradient-to-br from-stone-50/80 via-paper to-stone-50/40 px-5 py-4 md:px-8">
        <FunnelStepper steps={visibleSteps} current={visibleIndex} />
      </div>

      {/* ── Body — contenu de l'étape ─────────────────────── */}
      <div className="relative px-5 py-6 md:px-8 md:py-8">
        {canGoBack && (
          <button
            type="button"
            onClick={goBack}
            className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] font-medium text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
            Retour
          </button>
        )}

        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div
            key={step}
            id="aids-step-content"
            tabIndex={-1}
            className="outline-none"
            custom={dir}
            initial={{ opacity: 0, x: dir * 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -24 }}
            transition={{ duration: 0.28, ease: easeStripe }}
          >
            {step === 0 && <Step1Profile />}
            {step === 1 && <Step2Logement />}
            {step === 2 && needsRevenus && <Step3Revenus />}
            {step === 3 && <Step4Travaux />}
            {step === 4 && <Step5Contact />}
            {step === 5 && <Step6Resultat />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
